import { Trans, useTranslation } from 'next-i18next'

import {
  Heading3,
  LinkItem,
  LinkList,
  LinkText,
  Paragraph,
  Section3
} from '@/features/Listings/seo/elements'
import AverageRentFaq from '@/features/Listings/seo/search-results-content/content/average-rent-faq'
import ContentTemplate from '@/features/Listings/seo/search-results-content/content/city/content-template'
import getFilterLinks from '@/features/Listings/seo/search-results-content/content/get-filter-links'

import Amenities from '../amenities'
import RentingIn from '../renting-in'

const CowichanValley = ({
  filterSlug,
  isCanadaPage,
  cityName,
  cityNameWithState,
  cityData,
  isChinese
}) => {
  const { t } = useTranslation()
  const { state, slug: citySlug } = cityData
  const links = getFilterLinks(isCanadaPage, citySlug)

  const BcNeighbourhoodsLink =
    'https://liv.rent/blog/neighbourhoods/british-columbia-neighbourhoods/'

  const cheapNeighbourhoodsLink = isChinese
    ? 'https://liv.rent/blog/zh/%e8%a1%97%e5%8c%ba%e6%8c%87%e5%8d%97/%e7%a7%9f%e6%88%bf%e5%ae%9d%e5%85%b8-%e5%a4%a7%e6%b8%a9%e5%93%a5%e5%8d%8e%e7%a7%9f%e9%87%91%e6%9c%80%e4%be%bf%e5%ae%9c%e7%9a%8410%e4%b8%aa%e5%9c%b0%e5%8c%ba/'
    : 'https://liv.rent/blog/neighbourhoods/2020-renting-in-canada-what-1800-gets-you-across-canada/'

  const getAbout = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.cowichan-valley:About Cowichan Valley Paragraph 1">
            Nestled on Vancouver Island within the realm of{' '}
            <LinkText href="/rental-listings/british-columbia">
              British Columbia (BC)
            </LinkText>
            , Cowichan Valley captivates with its coastal allure and vibrant
            cultural scene. Embraced by the Cowichan Valley Glacier and Salish
            Sea, the town offers a haven for outdoor enthusiasts, encompassing
            activities from hiking to marine life exploration.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.cowichan-valley:About Cowichan Valley Paragraph 2">
            Cowichan Valley Marina and its arts and culture hub showcase the
            town’s identity, while its charming downtown exudes warmth. This
            close-knit community thrives through local events and festivals,
            fostering camaraderie. While the cost of living and ferry access
            present considerations, Cowichan Valley stands as a harmonious
            intersection of nature and community, embodying the essence of
            captivating{' '}
            <LinkText href={BcNeighbourhoodsLink}>
              cities and neighbourhoods in BC
            </LinkText>
            .
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentingIn = () => {
    return (
      <RentingIn
        filterSlug={filterSlug}
        cityName={cityName}
        rentingTipsLink={cheapNeighbourhoodsLink}
      />
    )
  }

  const getAverageRent = () => {
    return (
      <AverageRentFaq
        filterSlug={filterSlug}
        isCanadaPage={isCanadaPage}
        cityName={cityName}
        cityNameWithState={cityNameWithState}
        citySlug={citySlug}
        rentAmounts={{
          bed1: '$1,300 - $2,300',
          bed2: '$1,600 - $2,800',
          bed3: '$2,200 - $3,500'
        }}
        rentReportCity={t('cities:city_name_vancouver')}
        rentReportLink="https://liv.rent/blog/rent-reports/vancouver/"
        rentReportLinkZh="https://liv.rent/blog/zh/%E7%A7%9F%E9%87%91%E7%BB%9F%E8%AE%A1/%E6%B8%A9%E5%93%A5%E5%8D%8E/"
      />
    )
  }

  const getApartmentHunting = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.cowichan-valley:Apartment Hunting in Cowichan Valley Paragraph">
            Compared to cities like{' '}
            <LinkText href="/rental-listings/city/vancouver">
              Vancouver
            </LinkText>{' '}
            and{' '}
            <LinkText href="/rental-listings/city/richmond">Richmond</LinkText>,
            it’s easier to find{' '}
            <LinkText href={links.under1500}>
              cheap rentals in Cowichan Valley
            </LinkText>{' '}
            – although{' '}
            <LinkText href={links.under1000}>
              apartments for rent under $1000
            </LinkText>{' '}
            are still uncommon.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo:Apartment Hunting in LOCATION Paragraph 2">
            In addition to choosing your location, it’s important to know what
            rental types are available in {{ cityName }} (e.g.{' '}
            <LinkText href={links.bed1}>1 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed2}>2 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.bed3}>3 bedroom apartments</LinkText>,{' '}
            <LinkText href={links.studio}>studio apartments</LinkText>) and the
            average rent for each property type. Refer below to figure out your
            options - ranging from{' '}
            <LinkText href={links.furnished}>furnished rentals</LinkText> and{' '}
            <LinkText href={links.unfurnished}>unfurnished rentals</LinkText> to{' '}
            <LinkText href={links.longTerm}>long term rentals</LinkText> and{' '}
            <LinkText href={links.shortTerm}>short term rentals</LinkText>.
          </Trans>
        </Paragraph>
      </>
    )
  }

  const getRentalTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Apartments for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Apartments for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cowichan-valley:Apartments for rent Cowichan Valley Paragraph">
              Renting an apartment in the Cowichan Valley often appeals to those
              seeking a low-maintenance lifestyle.{' '}
              <LinkText href={links.apartment}>
                Apartments for rent in the Cowichan Valley
              </LinkText>{' '}
              are typically found in smaller complexes compared to larger urban
              centers, offering convenience and efficiency. They are ideal for
              singles, couples, or small families who value being close to local
              amenities like shops, cafes, and community centers.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Houses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Houses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cowichan-valley:Houses for rent Cowichan Valley Paragraph">
              <LinkText href={links.house}>
                Houses for rent in the Cowichan Valley
              </LinkText>{' '}
              cater to those desiring more space and privacy. This option is
              popular among families and those who enjoy having outdoor space
              for gardening or recreation. The houses in this area range from
              older, character-filled homes to newer constructions, each
              offering a unique charm and often integrating elements of the
              natural surroundings.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Townhouses for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Townhouses for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cowichan-valley:Townhouses for rent Cowichan Valley Paragraph">
              <LinkText href={links.townhouse}>
                Townhouses for rent in the Cowichan Valley
              </LinkText>{' '}
              are a blend of communal and private living. These are often part
              of smaller developments and appeal to small families or
              professionals. They typically offer more space than apartments but
              with less maintenance than a detached house, often including
              communal amenities like playgrounds or gardens.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Basements for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Basements for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cowichan-valley:Basements for rent Cowichan Valley Paragraph">
              Basement suites are a common, budget-friendly rental option in the
              Cowichan Valley. Basements for rent in the Cowichan Valley are
              usually part of a larger home, offer an affordable alternative for
              students, singles, or young professionals. While they might have
              limited natural light, they often provide a cozy and private
              living space.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Condos for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Condos for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cowichan-valley:Condos for rent Cowichan Valley Paragraph">
              <LinkText href={links.apartment}>
                Condos for rent in the Cowichan Valley
              </LinkText>{' '}
              attract those looking for a blend of modern living and low
              maintenance. Often located in strategic areas with good access to
              local amenities, condos here might offer additional facilities
              such as fitness centers or shared social spaces, appealing to
              young professionals and retirees alike.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Rooms for rent LOCATION"
              values={{
                location: cityName
              }}
              defaults="Rooms for rent {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cowichan-valley:Rooms for rent Cowichan Valley Paragraph">
              Renting a room is the most economical option in the Cowichan
              Valley, suitable for students, seasonal workers, or individuals
              seeking a flexible living arrangement.{' '}
              <LinkText href={links.room}>
                Rooms for rent in the Cowichan Valley
              </LinkText>{' '}
              typically involves shared use of common areas like the kitchen and
              living room, offering a communal and often sociable living
              experience.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getSpecialTypes = () => {
    return (
      <>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Pet friendly rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Pet friendly rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Pet friendly rentals CITY Paragraph">
              Though not every landlord is keen on allowing pets, there are a
              good number of{' '}
              <LinkText href={links.petFriendly}>
                pet-friendly rentals in {{ cityName }}
              </LinkText>{' '}
              for those with furry friends. Both{' '}
              <LinkText href={links.dogFriendly}>dog-friendly</LinkText> and{' '}
              <LinkText href={links.catFriendly}>cat-friendly</LinkText> rental
              units are available, as well as properties where both are allowed.
              Keep in mind that some {{ cityName }} neighbourhoods are better
              suited to those with pets, and adjust your search accordingly.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Furnished rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Furnished rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo:Furnished rentals LOCATION Paragraph">
              If you’re new to town or just haven’t had a chance to pick up
              furniture of your own yet, not to worry – there are plenty of{' '}
              <LinkText href={links.furnished}>furnished rentals</LinkText>{' '}
              available in {{ cityName }}. Renters seeking furnished units
              should search for these units using filters, but do be prepared to
              pay more in rent. There are{' '}
              <LinkText href="https://liv.rent/blog/renters/renting-furnished-unfurnished/">
                pros and cons to renting furnished units compared to unfurnished
                units
              </LinkText>
              , so deciding whether it’s worth it to buy furniture when moving
              is a good first step.
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Short term rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Short term rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cowichan-valley:Short term rentals Cowichan Valley Paragraph">
              The Cowichan Valley, known for its diverse economy and educational
              options, may attract some interest in{' '}
              <LinkText href={links.shortTerm}>short-term rentals</LinkText>,
              though its popularity for such rentals can vary and might not be
              as pronounced as in larger urban centers. While short-term rentals
              tend to be more popular in many cities, especially during peak
              periods like summer and the start of school semesters, this trend
              can differ in smaller regions like the Cowichan Valley. The
              flexibility offered by short-term rentals remains an appealing
              aspect for numerous renters, playing a crucial role in their
              decision-making process about{' '}
              <LinkText href="https://liv.rent/blog/landlords/short-term-vs-long-term-rentals-which-are-right-for-you/">
                whether short-term rentals suit their needs
              </LinkText>
              .
            </Trans>
          </Paragraph>
        </Section3>
        <Section3>
          <Heading3>
            <Trans
              i18nKey="seo:Cheap rentals in LOCATION"
              values={{
                location: cityName
              }}
              defaults="Cheap rentals in {{ location }}"
            />
          </Heading3>
          <Paragraph>
            <Trans i18nKey="seo.city.cowichan-valley:Cheap rentals Cowichan Valley Paragraph">
              If you’re looking for{' '}
              <LinkText href={links.under1000}>
                cheap rentals in Cowichan Valley
              </LinkText>
              , try finding a roommate or two and explore shared housing; or
              search for <LinkText href={links.room}>rooms for rent</LinkText>.
              It’s worth noting that in Cowichan Valley, there are
              neighbourhoods where rent is considerably cheaper, so exploring
              these options is a good idea as well.
            </Trans>
          </Paragraph>
        </Section3>
      </>
    )
  }

  const getLiving = () => {
    return (
      <>
        <Paragraph>
          <Trans i18nKey="seo.city.cowichan-valley:Living in Cowichan Valley Paragraph">
            Living in Cowichan Valley, British Columbia, offers a unique blend
            of small-town charm and stunning natural beauty. Nestled in the
            heart of Vancouver Island, this region is known for its lush
            forests, picturesque lakes, and the majestic Mount Prevost. Cowichan
            Valley’s community is warm and welcoming, providing a range of
            amenities for both residents and visitors. From farm-to-table dining
            experiences to a thriving arts and culture scene, and a plethora of
            outdoor activities, Cowichan Valley has something for everyone. The
            region’s exceptional quality of life is defined by its clean air,
            safe neighborhoods, and a laid-back lifestyle that sets it apart.
            Its breathtaking landscapes and tranquil ambiance make Cowichan
            Valley an ideal place for those seeking a serene and peaceful living
            environment.
          </Trans>
        </Paragraph>
        <Paragraph>
          <Trans i18nKey="seo.city.cowichan-valley:Living in Cowichan Valley Paragraph 2">
            If you’re not sure that the Cowichan Valley is the right place for
            you, check out some of these resources comparing the different
            cities.
          </Trans>
        </Paragraph>
        {isChinese ? (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e5%8a%a0%e6%8b%bf%e5%a4%a7%e7%95%99%e5%ad%a65%e5%a4%a7%e5%9f%8e%e5%b8%82-%e7%a7%9f%e6%88%bf%e3%80%81%e5%ad%a6%e8%b4%b9%e3%80%81%e5%b7%a5%e4%bd%9c%e3%80%81%e7%ad%be%e8%af%81%e8%af%a6%e8%a7%a3/">
              加拿大最佳移民城市
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%e7%a7%9f%e5%ae%a2/%e6%b8%a9%e5%93%a5%e5%8d%8e%e3%80%81%e5%a4%9a%e4%bc%a6%e5%a4%9a%e6%af%94%e8%be%83/">
              温哥华 vs 多伦多：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/%E8%A1%97%E5%8C%BA%E6%8C%87%E5%8D%97/%E6%B8%A9%E5%93%A5%E5%8D%8E%E5%92%8C%E7%B4%A0%E9%87%8C%E5%B1%85%E4%BD%8F%E6%88%90%E6%9C%AC%E6%AF%94%E8%BE%83/">
              温哥华 vs 素里：生活成本比较
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/zh/toronto-zh/2021%e7%89%88%e5%a4%9a%e4%bc%a6%e5%a4%9a%e3%80%81%e8%92%99%e7%89%b9%e5%88%a9%e5%b0%94%e7%94%9f%e6%b4%bb%e6%88%90%e6%9c%ac%e6%af%94%e8%be%83/">
              多伦多 vs 蒙特利尔：生活成本比较
            </LinkItem>
          </LinkList>
        ) : (
          <LinkList>
            <LinkItem href="https://liv.rent/blog/2020/11/best-canadian-cities-for-immigration/">
              Best Canadian cities for immigration
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2019/11/vancouver-or-toronto-a-cost-of-living-comparison/">
              Vancouver vs Toronto: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/02/surrey-vs-vancouver-bc-comparison/">
              Vancouver vs Surrey: Cost of living comparison
            </LinkItem>
            <LinkItem href="https://liv.rent/blog/2021/04/toronto-vs-montreal-cost-of-living-comparison/">
              Toronto vs Montreal: Cost of living comparison
            </LinkItem>
          </LinkList>
        )}
      </>
    )
  }

  const getPopularHoods = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.cowichan-valley:Popular Neighbourhoods Cowichan Valley Paragraph">
          Like any city, Cowichan Valley is made up of smaller neighbourhoods
          with their own distinct features. Some of the most popular
          neighbourhoods include Duncan, Cobble Hill, Chemainus, Mill Bay, Lake
          Cowichan, Maple Bay, Cowichan Bay, and Shawnigan Lake. For more info
          about Cowichan Valley, you can consult this guide on{' '}
          <LinkText href={BcNeighbourhoodsLink}>BC neighbourhoods</LinkText>.
        </Trans>
      </Paragraph>
    )
  }

  const getLandmarks = () => {
    return (
      <Paragraph>
        <Trans i18nKey="seo.city.cowichan-valley:Local Landmarks Cowichan Valley Paragraph">
          The Cowichan Valley, located in British Columbia, is home to a
          plethora of local landmarks that celebrate the region’s natural beauty
          and historical significance. One such landmark is the Cowichan Bay
          Maritime Centre, where visitors can explore the maritime heritage of
          the area and even take part in boat-building workshops. The Kinsol
          Trestle, a majestic wooden railway bridge, is another notable landmark
          that offers stunning views of the surrounding forests and rivers. For
          those interested in history, the Cowichan Valley Museum provides a
          captivating glimpse into the region’s past, while the Duncan Totem
          Poles showcase the rich indigenous culture of the area. Finally, the
          stunning vistas from Mount Tzouhalem and the tranquil atmosphere of
          the Cowichan River Provincial Park make them must-visit destinations
          for nature lovers. These local landmarks contribute to the unique
          charm and allure of the Cowichan Valley.
        </Trans>
      </Paragraph>
    )
  }

  const content = {
    about: getAbout(),
    rentingIn: getRentingIn(),
    averageRent: getAverageRent(),
    apartmentHunting: getApartmentHunting(),
    rentalTypes: getRentalTypes(),
    specialTypes: getSpecialTypes(),
    amenities: (
      <Amenities cityName={cityName} filterSlug={filterSlug} links={links} />
    ),
    living: getLiving(),
    popularHoods: getPopularHoods(),
    landmarks: getLandmarks()
  }

  return (
    <ContentTemplate
      content={content}
      filterSlug={filterSlug}
      isCanadaPage={isCanadaPage}
      cityName={cityName}
      cityNameWithState={cityNameWithState}
      citySlug={citySlug}
      state={state}
    />
  )
}

export default CowichanValley
